import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney, todayLocalIso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Truck, TrendingUp } from "lucide-react";

const MODALIDADES = [
  { value: "saldo_prepagado", label: "Saldo prepagado" },
  { value: "pago_por_guia", label: "Pago por guía" },
  { value: "propio", label: "Entrega propia" },
];

export default function Transportistas() {
  const [items, setItems] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", activo: true, modalidad: "pago_por_guia", saldo_inicial: 0 });
  const [editingId, setEditingId] = useState(null);
  const [recarga, setRecarga] = useState(null);
  const [rec, setRec] = useState({ monto: "", banco: "", caja_id: "", numero_factura: "", metodo_pago: "Transferencia", referencia: "", fecha: todayLocalIso() });

  const load = async () => {
    try {
      const { data } = await api.get("/transportistas");
      const withSaldos = await Promise.all(data.map(async (t) => {
        if (t.modalidad === "saldo_prepagado") {
          try {
            const { data: s } = await api.get(`/transportistas/${t.id}/saldo`);
            return { ...t, saldo_disponible: s.saldo_disponible, total_recargas: s.total_recargas, total_consumos: s.total_consumos };
          } catch { return t; }
        }
        return t;
      }));
      setItems(withSaldos);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); api.get("/cajas").then(r => setCajas(r.data)).catch(()=>{}); api.get("/bancos").then(r => setBancos(r.data)).catch(()=>{}); }, []);

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      if (editingId) {
        await api.patch(`/transportistas/${editingId}`, form);
        toast.success("Actualizada");
      } else {
        await api.post("/transportistas", form);
        toast.success("Creada");
      }
      setOpen(false); setEditingId(null); setForm({ nombre:"", activo:true, modalidad:"pago_por_guia", saldo_inicial:0 }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ nombre: t.nombre, activo: t.activo !== false, modalidad: t.modalidad || "pago_por_guia", saldo_inicial: t.saldo_inicial || 0 });
    setOpen(true);
  };
  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar ${t.nombre}?`)) return;
    try { await api.delete(`/transportistas/${t.id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveRecarga = async () => {
    const monto = Number(rec.monto);
    if (!monto || monto <= 0) { toast.error("Monto inválido"); return; }
    if (!rec.banco && !rec.caja_id) { toast.error("Elegí banco O caja"); return; }
    if (rec.banco && rec.caja_id) { toast.error("Elegí banco O caja, no ambos"); return; }
    try {
      await api.post("/transportistas/recargas", { ...rec, monto, transportista_id: recarga.id });
      toast.success("Recarga registrada");
      setRecarga(null); setRec({ monto:"", banco:"", caja_id:"", numero_factura:"", metodo_pago:"Transferencia", referencia:"", fecha: todayLocalIso() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="transportistas-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Logística</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Truck className="w-8 h-8" /> Empresas de Transporte
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Configurá cada empresa: prepagado, pago por guía o entrega propia.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm({ nombre:"", activo:true, modalidad:"pago_por_guia", saldo_inicial:0 }); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-transp-btn">
              <Plus className="w-4 h-4" /> Nueva empresa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Editar" : "Nueva"} empresa</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
              <div>
                <Label>Modalidad</Label>
                <Select value={form.modalidad} onValueChange={(v) => setForm({ ...form, modalidad: v })}>
                  <SelectTrigger data-testid="transp-modalidad"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODALIDADES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.modalidad === "saldo_prepagado" && (
                <div><Label>Saldo inicial (Q)</Label><Input type="number" step="0.01" value={form.saldo_inicial} onChange={(e) => setForm({ ...form, saldo_inicial: Number(e.target.value) })} /></div>
              )}
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div className="text-sm font-semibold">Activa</div>
                <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]">{editingId ? "Guardar" : "Crear"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(t => (
          <Card key={t.id} className="p-5 border-zinc-200 rounded-md" data-testid={`transp-${t.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-lg">{t.nombre}</div>
              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${t.activo ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{t.activo ? "Activa" : "Inactiva"}</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Modalidad</div>
            <div className="text-sm mb-3">{MODALIDADES.find(m => m.value === t.modalidad)?.label || t.modalidad || "Pago por guía"}</div>
            {t.modalidad === "saldo_prepagado" && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Saldo disponible</div>
                <div className={`font-manrope font-black text-3xl mt-1 tabular-nums ${(t.saldo_disponible || 0) < 100 ? "text-red-600" : "text-zinc-900"}`} data-testid={`transp-saldo-${t.id}`}>
                  {formatMoney(t.saldo_disponible || 0)}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div><span className="text-zinc-500">Recargado: </span><span className="font-semibold text-emerald-700">+{formatMoney(t.total_recargas || 0)}</span></div>
                  <div><span className="text-zinc-500">Consumido: </span><span className="font-semibold text-red-600">-{formatMoney(t.total_consumos || 0)}</span></div>
                </div>
                <Button size="sm" onClick={() => setRecarga(t)} className="w-full mt-3 gap-1 bg-[#002FA7] hover:bg-[#00227A]" data-testid={`recargar-${t.id}`}>
                  <TrendingUp className="w-3 h-3" /> Recargar saldo
                </Button>
              </>
            )}
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="flex-1">Editar</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(t)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Modal recarga */}
      <Dialog open={!!recarga} onOpenChange={(v) => !v && setRecarga(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recargar saldo — {recarga?.nombre}</DialogTitle>
            <DialogDescription>Se crea UN gasto (banco o caja) y aumenta el saldo disponible.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Fecha</Label><Input type="date" value={rec.fecha} onChange={(e) => setRec({ ...rec, fecha: e.target.value })} /></div>
            <div><Label>Monto (Q)</Label><Input type="number" step="0.01" value={rec.monto} onChange={(e) => setRec({ ...rec, monto: e.target.value })} data-testid="rec-monto" /></div>
            <div><Label>N° Factura</Label><Input value={rec.numero_factura} onChange={(e) => setRec({ ...rec, numero_factura: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Banco</Label>
                <Select value={rec.banco || "__none__"} onValueChange={(v) => setRec({ ...rec, banco: v === "__none__" ? "" : v, caja_id: "" })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {bancos.map(b => <SelectItem key={b.id} value={b.nombre}>{b.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>o Caja</Label>
                <Select value={rec.caja_id || "__none__"} onValueChange={(v) => setRec({ ...rec, caja_id: v === "__none__" ? "" : v, banco: "" })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {cajas.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Referencia</Label><Input value={rec.referencia} onChange={(e) => setRec({ ...rec, referencia: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecarga(null)}>Cancelar</Button>
            <Button onClick={saveRecarga} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-rec-btn">Registrar recarga</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
